import React from 'react';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';

import SquareButton from '@/ui-components/SquareButton';
import Icon from '@/ui-components/Icon';
import Logo from '@/ui-components/Logo';
import Typography from '@/ui-components/Typography';
import BaseButton from '@/ui-components/BaseButton';
import Link from '@/ui-components/Link';

import useLayout from '@/use/useLayout';
import useBem from '@/use/useBem';
import { openModal } from '@/use/useModal';
import { isMenuOpenState } from '@/use/useMenuState';
import { gameIdState } from '@/framework/game';
import { gamePhaseState } from '@/framework/board/boardState';
import { useExitGame } from '@/use/useGame';
import { closeModal } from '@/use/useModal';

import { userSelector } from '@/framework/user';

const Nav = () => {
  const navigate = useNavigate();
  const loggedIn = useRecoilValue(userSelector.loggedIn);
  const user = useRecoilValue(userSelector.user);
  const gameId = useRecoilValue(gameIdState);
  const gamePhase = useRecoilValue(gamePhaseState);
  const { layout } = useLayout();
  const { bem } = useBem('nav');
  const { exitGame } = useExitGame();
  const isMenuOpen = useRecoilValue(isMenuOpenState);

  const handleMenuClick = () => {
    if (gameId && gamePhase !== 'over') {
      openModal('confirm-exit');
    } else {
      exitGame();
      closeModal();
      navigate('/');
    }
  };

  const renderLogo = () => {
    return (
      <div className={bem('logo-wrapper')}>
        {isMenuOpen && (
          <div className={bem('social-button')}>
            <Typography variant="s" className={bem('social-button-text')}>
              Join our socials
            </Typography>
            <Link
              to="https://discord.gg/WzPZbNAGYK"
              target="__blank"
              rightIcon={
                <Icon name="discord" style={{ width: 28, height: 28 }} />
              }
              variant="flat"
            />
            <Link
              to="https://www.instagram.com/gems4.io/"
              target="__blank"
              rightIcon={
                <Icon name="instagram" style={{ width: 25, height: 25 }} />
              }
              variant="flat"
            />
          </div>
        )}

        {layout !== 'desktop' && (
          <div className={bem('logo-img')}>
            <Logo size="small" />
          </div>
        )}
      </div>
    );
  };

  const renderAccount = () => {
    if (gameId) {
      return;
    }

    return (
      <div className={bem('button', 'account')}>
        {loggedIn ? (
          <>
            <div className={bem('store-row')}>
              <SquareButton
                to="/store"
                icon={<Icon name="shopping-cart" />}
              >
                Store
              </SquareButton>
              <Typography variant="s" className={bem('sparks')}>
                {user.sparks ?? 0}
              </Typography>
            </div>
            <SquareButton to="/rewards" icon={<Icon name="crown" />}>
              Rewards
            </SquareButton>
            <SquareButton to="/profile" icon={<Icon name="user" />}>
              Profile
            </SquareButton>
          </>
        ) : (
          <SquareButton to="/login" icon={<Icon name="user" />}>
            Login
          </SquareButton>
        )}
      </div>
    );
  };

  const renderLeaderboard = () => {
    if (layout === 'desktop') {
      return null;
    }

    return (
      <div className={bem('button', 'leaderboard')}>
        <SquareButton
          onClick={() => {
            openModal('leaderboard');
          }}
          icon={<Icon name="crown" />}
        >
          Leaderboard
        </SquareButton>
      </div>
    );
  };

  const renderSecondaryMenu = () => {
    if (layout !== 'desktop' && !isMenuOpen) {
      return null;
    }

    return (
      <>
        {renderAccount()}
        {renderLeaderboard()}
      </>
    );
  };

  return (
    <div className={bem('', [layout, { menuState: isMenuOpen }])}>
      {renderLogo()}
      {renderSecondaryMenu()}

      <div className={bem('button', 'menu')}>
        <SquareButton onClick={handleMenuClick} icon={<Icon name="list" />}>
          Menu
        </SquareButton>
      </div>
    </div>
  );
};

export default Nav;
